import { Link } from 'react-router-dom'
import { m } from 'motion/react'
import { Button } from '@/components/ui'
import { useSEO, createServiceSchema } from '@/hooks/useSEO'
import { useHeroReveal } from '@gjl/motion/css'
import { generateBreadcrumbs, breadcrumbLabels } from '@/components/Breadcrumbs'
import {
  CREATIVE_LEAD,
  DESIGN_ADDONS,
  DESIGN_PROJECTS,
  PARISH_DESIGN_PROJECTS,
  managedService,
} from '@gjl/voice/pricing'

// Prices render from @gjl/voice/pricing. Edit them there.
const managedDesign = managedService('managed-design')

const INLINE_LINK =
  'text-primary underline decoration-1 underline-offset-4 hover:decoration-2 focus-visible:outline-primary focus-visible:outline-2 focus-visible:outline-offset-4'

const PROBLEMS = [
  {
    problem: 'Every poster looks like a different organization',
    description:
      'One volunteer made the fall poster, the office made the spring one, and neither matches the website. Donors and presenters notice the drift even when they cannot name it.',
  },
  {
    problem: 'Design lands on whoever has an evening free',
    description:
      'Your section leader with Canva is generous, not a designer. The hours they spend fighting templates are hours they are not doing what you actually brought them in to do.',
  },
  {
    problem: 'Season materials arrive in a rush',
    description:
      'Programs get finished the week of the concert and posters go up late. The design was always going to happen; it just never got the time it needed.',
  },
  {
    problem: 'Your logo does not survive contact with print',
    description:
      'It was made for a website header, and now it is blurry on a banner and unreadable on a program spine. A working identity has to hold up everywhere your name appears.',
  },
]

const FAQS = [
  {
    q: 'Do we need to be a Fugue Lab website client?',
    a: 'No. Design projects stand on their own. They do pair naturally with a site we run, because new graphics land straight in your website, email, and social without a handoff.',
  },
  {
    q: 'What does Managed Design cover month to month?',
    a: `For $${managedDesign.priceMonthly} a month you send design requests as they come up: a concert announcement, a program cover, social graphics for the season. One request is active at a time, with a 72-hour turnaround.`,
  },
  {
    q: 'What if we need a whole season designed at once?',
    a: 'That is the season campaign project: announcement graphics, program covers, social templates, and print pieces designed together as one family, priced as a single engagement.',
  },
  {
    q: 'Who owns the work?',
    a: 'You do. Source files and print-ready exports are yours, delivered in the formats your printers and platforms ask for. Same policy as everything else here: you can leave with everything.',
  },
]

export default function GraphicDesignPage() {
  const breadcrumbs = generateBreadcrumbs('/services/design', breadcrumbLabels)

  useSEO({
    title: 'Graphic Design for Music Organizations',
    // The retainer price interpolates from pricing.ts. "$1,500" is still a
    // literal: DESIGN_PROJECTS has no typed accessor to read it from.
    description: `Brand identity, posters, concert programs, and season campaigns for music organizations. Brand projects from $1,500, or a design retainer at $${managedDesign.priceMonthly}/mo.`,
    url: '/services/design',
    canonical: '/services/design',
    breadcrumbs,
    schema: createServiceSchema({
      name: 'Graphic Design for Music Organizations',
      description:
        'Brand identity, print, and season campaign design for festivals, churches, choruses, and music schools.',
      url: '/services/design',
    }),
  })

  // The hero keeps its entrance; everything below the fold is static. The
  // per-section staggered reveals and card hover lifts were the stock kit.
  const heroRef = useHeroReveal<HTMLDivElement>(0.2)

  return (
    <div className="mx-auto max-w-6xl space-y-28 px-5 py-16 md:space-y-36 md:px-12">
      {/* Hero */}
      <section ref={heroRef} className="mx-auto max-w-4xl space-y-6 text-center">
        <h1 className="text-fluid-h1">Design that carries your season</h1>
        <p className="text-fluid-h4 text-muted-foreground mx-auto max-w-3xl">
          Your audience meets the poster before the performance. Brand identity, print, and campaign design made for
          music organizations, by a working artist.
        </p>
        <p className="text-fluid-body text-muted mx-auto max-w-2xl">
          One-time projects, a monthly design retainer, or both. Everything delivered in the formats your printers and
          platforms ask for.
        </p>
        <div className="flex flex-col items-center justify-center gap-4 pt-4 sm:flex-row">
          <a href="/book" target="_blank" rel="noopener noreferrer">
            <m.div
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              transition={{ type: 'spring', stiffness: 400, damping: 25 }}
            >
              <Button variant="default" size="lg" className="cursor-pointer px-8 py-6 text-lg">
                Book a Free Call →
              </Button>
            </m.div>
          </a>
          <Link to="/services/photography">
            <Button variant="outline" size="lg" className="cursor-pointer px-8 py-6 text-lg">
              Photography
            </Button>
          </Link>
        </div>
      </section>

      {/* Problems: a typographic list, name left and explanation right, in
          place of four bordered cards. Space alone separates the rows. */}
      <section aria-labelledby="problems" className="space-y-12">
        <h2 id="problems" className="text-fluid-h3 max-w-3xl">
          The gap between how you sound and how you look
        </h2>
        <ul className="space-y-10 md:space-y-12">
          {PROBLEMS.map((item) => (
            <li key={item.problem} className="grid gap-3 md:grid-cols-12 md:items-baseline md:gap-8">
              <h3 className="text-fluid-h4 md:col-span-5">{item.problem}</h3>
              <p className="text-muted-foreground max-w-[60ch] leading-relaxed md:col-span-7">{item.description}</p>
            </li>
          ))}
        </ul>
      </section>

      {/* Who does the work */}
      <section aria-labelledby="who-does-the-work" className="grid gap-6 md:grid-cols-12 md:gap-8">
        <h2 id="who-does-the-work" className="text-fluid-h3 md:col-span-5">
          An artist, not an agency queue
        </h2>
        <div className="text-muted-foreground space-y-4 leading-relaxed md:col-span-7">
          <p className="text-fluid-body max-w-[60ch]">{CREATIVE_LEAD.credit}</p>
          <p className="max-w-[60ch]">
            A season designed by one hand stays one family from the first poster to the last program, and every request
            goes to the person who will actually draw it.
          </p>
        </div>
      </section>

      {/* Projects: a priced catalog of distinct engagements, not plans to
          compare, so it is set as a typographic list rather than a card grid. */}
      <section aria-labelledby="projects" className="space-y-12">
        <h2 id="projects" className="text-fluid-h3">
          The projects
        </h2>
        <ul className="grid gap-x-10 gap-y-14 md:grid-cols-2 lg:grid-cols-3">
          {DESIGN_PROJECTS.map((project) => (
            <li key={project.id} className="space-y-3">
              <h3 className="text-fluid-h4">{project.name}</h3>
              <p className="font-medium">{project.price}</p>
              <p className="text-muted-foreground max-w-[48ch] leading-relaxed">{project.note}</p>
            </li>
          ))}
          <li className="space-y-3">
            <h3 className="text-fluid-h4">{managedDesign.name}</h3>
            <p className="text-muted-foreground text-sm">Done for you</p>
            <p className="font-medium">${managedDesign.priceMonthly}/mo</p>
            <p className="text-muted-foreground max-w-[48ch] leading-relaxed">{managedDesign.note}</p>
            <Link to="/services/done-for-you#managed-design" className={`${INLINE_LINK} inline-block`}>
              Learn more about {managedDesign.name}
            </Link>
          </li>
        </ul>
        <p className="text-muted-foreground max-w-[60ch] leading-relaxed">
          Want the whole visual side handled alongside your website and email? That is{' '}
          <Link to="/services/done-for-you#concierge" className={INLINE_LINK}>
            Concierge
          </Link>
          , where this capacity is already included.
        </p>
      </section>

      {/* Add-ons */}
      <section aria-labelledby="add-ons" className="space-y-12">
        <h2 id="add-ons" className="text-fluid-h3">
          Add to any project
        </h2>
        <ul className="grid gap-x-10 gap-y-14 md:grid-cols-2 lg:grid-cols-3">
          {DESIGN_ADDONS.map((item) => (
            <li key={item.id} className="space-y-3">
              <h3 className="text-fluid-h4">{item.name}</h3>
              <p className="font-medium">{item.price}</p>
              <p className="text-muted-foreground max-w-[48ch] leading-relaxed">{item.note}</p>
            </li>
          ))}
        </ul>
      </section>

      {/* Parish lane */}
      <section aria-labelledby="parishes" className="space-y-12">
        <div className="grid gap-6 md:grid-cols-12 md:gap-8">
          <h2 id="parishes" className="text-fluid-h3 md:col-span-5">
            For parishes
          </h2>
          <p className="text-muted-foreground text-fluid-body max-w-[60ch] leading-relaxed md:col-span-7">
            Church music budgets work differently, so parish work is priced differently. Most of it starts with the
            concert series or the choir season rather than the whole parish identity.
          </p>
        </div>
        <ul className="grid gap-x-10 gap-y-14 md:grid-cols-3">
          {PARISH_DESIGN_PROJECTS.map((item) => (
            <li key={item.id} className="space-y-3">
              <h3 className="text-fluid-h4">{item.name}</h3>
              <p className="font-medium">{item.price}</p>
              <p className="text-muted-foreground max-w-[48ch] leading-relaxed">{item.note}</p>
            </li>
          ))}
        </ul>
      </section>

      {/* FAQ */}
      <section aria-labelledby="questions" className="space-y-12">
        <h2 id="questions" className="text-fluid-h3">
          Questions
        </h2>
        <ul className="space-y-10 md:space-y-12">
          {FAQS.map((f) => (
            <li key={f.q} className="grid gap-3 md:grid-cols-12 md:gap-8">
              <h3 className="text-xl leading-snug md:col-span-5">{f.q}</h3>
              <p className="text-muted-foreground max-w-[60ch] leading-relaxed md:col-span-7">{f.a}</p>
            </li>
          ))}
        </ul>
        <div className="space-y-3">
          <a href="/book" target="_blank" rel="noopener noreferrer" className="inline-block">
            <Button size="lg" className="cursor-pointer">
              Book a Free Call →
            </Button>
          </a>
          <p className="text-muted-foreground text-sm">
            Every project ends with you owning the files, full resolution, no strings.
          </p>
        </div>
      </section>
    </div>
  )
}
